// Hermes browser actions: floating approval card. When an agent wants to
// submit / send / pay / delete on a web page, it stops and waits here for the
// user. Module scope (App-level components remount on every App render).
// Polls only when the server has a browser configured.
import { useEffect, useRef, useState } from "react";

const POLL_MS = 4000;

export default function BrowserApprovals({ authToken, S, showToast }) {
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [zoom, setZoom] = useState(null);
  const seen = useRef(new Set());

  useEffect(() => {
    if (!authToken) return;
    let dead = false;
    fetch("/api/hermes/browser/status", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (!dead) setEnabled(!!(d && d.enabled)); }).catch(() => {});
    return () => { dead = true; };
  }, [authToken]);

  useEffect(() => {
    if (!enabled || !authToken) return;
    let dead = false;
    const load = () => {
      if (document.visibilityState === "hidden") return;
      fetch("/api/hermes/browser/approvals", { headers: { Authorization: `Bearer ${authToken}` }, cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (dead || !d) return;
          const list = d.approvals || [];
          for (const a of list) if (!seen.current.has(a.id)) { seen.current.add(a.id); showToast && showToast("Hermes is waiting for your approval"); }
          setItems(list);
        }).catch(() => {});
    };
    load();
    const t = setInterval(load, POLL_MS);
    document.addEventListener("visibilitychange", load);
    return () => { dead = true; clearInterval(t); document.removeEventListener("visibilitychange", load); };
  }, [enabled, authToken]);

  const decide = async (a, approve) => {
    setBusy(a.id);
    try {
      const r = await fetch(`/api/hermes/browser/approvals/${encodeURIComponent(a.id)}`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ approve }),
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      setItems((p) => p.filter((x) => x.id !== a.id));
      showToast && showToast(approve ? "Approved — Hermes will continue" : "Declined — Hermes won't do it");
    } catch (e) { showToast && showToast("Couldn't save your decision: " + e.message); }
    setBusy(null);
  };

  if (!enabled || !items.length) return null;
  const host = (u) => { try { return new URL(u).host; } catch { return u; } };
  return (
    <div className="cf-browser-approvals" style={{ position: "fixed", right: 16, bottom: 16, zIndex: 9000, width: "min(380px, calc(100vw - 32px))", display: "flex", flexDirection: "column", gap: 10 }}>
      {items.slice(0, 3).map((a) => (
        <div key={a.id} role="alertdialog" aria-label="Approve browser action" className="cf-slide-in-right"
          style={{ background: S.modalBg || S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 14, padding: 14, boxShadow: "0 12px 32px rgba(0,0,0,.28)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#e0a030" }}>Approval needed</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: S.text, marginTop: 4, lineHeight: 1.4 }}>
            Hermes wants to {a.action} “{a.label}”
          </div>
          <div style={{ fontSize: 12, color: S.text3, marginTop: 2, wordBreak: "break-all" }}>on {a.title ? `${a.title} · ` : ""}{host(a.url)}</div>
          {a.screenshot && (
            <img alt="Page Hermes is acting on" src={`data:image/jpeg;base64,${a.screenshot}`} onClick={() => setZoom(a.screenshot)}
              style={{ display: "block", width: "100%", maxHeight: 180, objectFit: "cover", objectPosition: "top", borderRadius: 8, marginTop: 10, border: `1px solid ${S.border2}`, cursor: "zoom-in" }} />
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button disabled={busy === a.id} onClick={() => decide(a, true)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 9, border: "none", background: "#2f9e62", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Approve</button>
            <button disabled={busy === a.id} onClick={() => decide(a, false)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 9, border: `1px solid ${S.border2}`, background: "transparent", color: S.text, fontWeight: 700, cursor: "pointer" }}>Decline</button>
          </div>
        </div>
      ))}
      {items.length > 3 && <div style={{ fontSize: 12, color: S.text3, textAlign: "right" }}>+{items.length - 3} more waiting</div>}
      {zoom && (
        <div className="cf-modal-backdrop" onClick={() => setZoom(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9001, cursor: "zoom-out" }}>
          <img alt="Page screenshot" src={`data:image/jpeg;base64,${zoom}`} style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 10 }} />
        </div>
      )}
    </div>
  );
}
